import { useCallback, useEffect, useState } from "react";
import ProductPermissionsRow from "../product-permissions-row";

import { useIntl } from "react-intl";
import { Header, Icon, Segment } from "semantic-ui-react";
import { Config } from "../../../../config/api";
import { useDebounce } from "../../../../libs/component_utils";
import PermissionsService from "../../../../services/permissions";
import { usePermissionsContext } from "../../index/context";

const permissionsService = new PermissionsService();

const groupPermissionsByAgencyId = (data) => {
  return data.reduce((acc, cur) => {
    if (acc[cur.agency_id]) {
      acc[cur.agency_id].push(cur);
    } else {
      acc[cur.agency_id] = [cur];
    }
    return acc;
  }, {});
};

export const ProductPermissionsGrid = () => {
  const [index, setIndex] = useState(-1);
  const [permissions, setPermissions] = useState([]);
  const [filterState, setFilterState] = useState({
    results: [],
    hasFiltered: false,
  });
  const { successDeleteMessage, searchQuery } = usePermissionsContext();
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const intl = useIntl();
  const handleChange = (id) => setIndex(index === id ? -1 : id);

  const fetchPermissions = useCallback(async () => {
    setPermissionsLoading(true);
    try {
      const response = await permissionsService.getAllPermissions();
      const groupedPermissions = groupPermissionsByAgencyId(response.data ?? []);
      const sortedPermissions = Object.values(groupedPermissions).sort((a, b) =>
        a[0].agency_name.localeCompare(b[0].agency_name),
      );
      setPermissions(sortedPermissions);
    } catch (error) {
      console.error("Error fetching permissions:", error);
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  const handleSearch = useCallback(
    (searchQuery) => {
      if (!searchQuery) {
        setFilterState({ results: [], hasFiltered: false });
        return;
      }

      const filtered = permissions.filter((permission) => {
        const agencyName = permission[0].agency_name.toLowerCase();
        return agencyName.includes(searchQuery.toLowerCase());
      });
      setFilterState({ results: filtered, hasFiltered: true });
    },
    [permissions],
  );

  const debouncedSearch = useDebounce(Config.search_debounce_delay, handleSearch);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  useEffect(() => {
    debouncedSearch(searchQuery);
  }, [searchQuery, debouncedSearch]);

  useEffect(() => {
    // If a permission was successfully deleted, refetch the permissions
    if (successDeleteMessage) {
      fetchPermissions();
    }
  }, [successDeleteMessage, fetchPermissions]);

  return (
    <Segment
      basic
      loading={permissionsLoading}
      style={{ padding: "0" }}
    >
      {(filterState.hasFiltered ? filterState.results : permissions).map((item) => (
        <ProductPermissionsRow
          agency_id={item[0].agency_id}
          agency_name={item[0].agency_name}
          index={index}
          permissionRows={item}
          key={item[0].id}
          id={item[0].id}
          handleChange={handleChange}
        />
      ))}
      {filterState.hasFiltered && filterState.results.length === 0 && (
        <Segment placeholder>
          <Header icon>
            <Icon name="search" />
            <Header.Content style={{ marginTop: "1rem" }}>
              {intl.formatMessage({
                id: "HINT_NO_AGENCY_FOUND",
                defaultMessage: "We found no agency matching your query.",
              })}
            </Header.Content>
          </Header>
        </Segment>
      )}
      {permissions.length === 0 && !permissionsLoading && (
        <Segment placeholder>
          <Header icon>
            <Icon name="add" />
            <Header.Content style={{ marginTop: "1rem" }}>
              {intl.formatMessage({
                id: "HINT_PERMISSION_GET_STARTED",
                defaultMessage: "Please add a permission to get started.",
              })}
            </Header.Content>
          </Header>
        </Segment>
      )}
    </Segment>
  );
};

export default ProductPermissionsGrid;
